import React from 'react'
import { ApolloClient, gql, useQuery } from '@apollo/client'
import { Switch, Case, Default } from 'react-if'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import { RetryFallback } from '@/molecules/RetryFallback'
import type {
  GetProjectBonusVideosByPageQuery,
  GetProjectBonusVideosByPageQueryVariables,
} from '@/types/codegen-federation'
import { createPlaceholderArray } from '@/utils/PlaceholderArrays'
import { useTranslate } from '@/utils/translate/translate-client'

const loadingTiles = createPlaceholderArray(4)
const PLACEHOLDER_CHILDREN = loadingTiles.map((tile) => <PlaceholderMediaCard key={tile} />)

export interface VideoGridProps {
  projectSlug: string
  page: string
  excludeGuid?: string
  client?: ApolloClient<object>
}

export const getVideoRailQuery = gql`
  query getProjectBonusVideosByPage($projectSlug: String!) {
    listWatchables(
      first: 50
      input: { filter: { projectSlug: { eq: $projectSlug }, type: { in: [BONUS_CONTENT, COMMUNITY_VIDEO] } } }
    ) {
      edges {
        node {
          __typename
          ... on ContentBonus {
            id
            name
            subtitle
            posterLandscapeCloudinaryPath
            url
            duration
            watchPosition {
              id
              position
            }
            page
          }
        }
      }
    }
  }
`

export const VideoGrid: React.FC<VideoGridProps> = ({ projectSlug, page, excludeGuid }) => {
  const { data, loading, refetch, error } = useQuery<
    GetProjectBonusVideosByPageQuery,
    GetProjectBonusVideosByPageQueryVariables
  >(getVideoRailQuery, {
    variables: { projectSlug },
    errorPolicy: 'all',
  })

  const { t } = useTranslate('watch')

  // Derive ContentBonus nodes from the connection, preserving order
  type ListWatchables = NonNullable<GetProjectBonusVideosByPageQuery['listWatchables']>
  type Edge = NonNullable<NonNullable<ListWatchables['edges']>[number]>
  type Node = NonNullable<Edge['node']>
  type BonusNode = Extract<Node, { __typename: 'ContentBonus' }>

  const videos: BonusNode[] = (data?.listWatchables?.edges ?? [])
    .map((edge): BonusNode | null => {
      const node = edge?.node
      return node && node.__typename === 'ContentBonus' ? node : null
    })
    .filter((n): n is BonusNode => n !== null)
    // Only keep bonus page content explicitly
    .filter((n) => n.page === 'bonus')
  const filteredVideos: BonusNode[] = videos.filter((video) => video.id !== excludeGuid)

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      <Switch>
        <Case condition={loading}>{PLACEHOLDER_CHILDREN}</Case>
        <Case condition={!!error}>
          <RetryFallback
            className="py-20 text-center sm:col-span-2 md:col-span-3 lg:col-span-2 lg:col-start-2"
            message={t('problemLoadingVideos', 'Oops! There was a problem loading videos.')}
            ctaAction={() => refetch?.()}
            ctaText={t('retry', 'Retry')}
          />
        </Case>
        <Case condition={filteredVideos?.length > 0}>
          {filteredVideos?.map((video) => {
            let progress = 0
            if (video?.watchPosition?.position && video.duration) {
              progress = (video.watchPosition.position / video.duration) * 100
            }

            return (
              <LandscapeTileCard
                className="mr-4"
                key={video.id}
                linkModel={{
                  linkUrl: `/watch/${projectSlug}/videos/${video.page ?? page}/${video.id}`,
                  alt: video.name ?? '',
                  imageUrl: video.posterLandscapeCloudinaryPath ?? '',
                  label: video.name ?? '',
                }}
              >
                <PlayButtonThumbnailOverlay />
                <ProgressOverlay progress={progress} />
              </LandscapeTileCard>
            )
          })}
        </Case>
        <Case condition={videos.length === 1 && filteredVideos?.length === 0} />
        <Default>{null}</Default>
      </Switch>
    </div>
  )
}
